import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from "@/components/ui/table";
import { Building2, Mail, Phone, MapPin, Users, FileText, ArrowLeft, Edit, Loader2, AlertCircle, User, History, ArrowRight, TrendingUp, Check, X, Receipt, Package, ChevronDown, CheckCircle2, IndianRupee, SlidersHorizontal, Plus } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { formatDate } from "@/lib/utils";
import { getCompanyById, resolveSeatRequest, provisionCompany, addCompanyPurchase, settleOfflinePayment, getCompanyBillingTrace, resendCompanyAdminInvite } from "@/services/superAdminService";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useProvisionCompany } from "@/components/super-admin/useProvisionCompany";
import ProvisionSubscriptionDialog from "@/components/super-admin/ProvisionSubscriptionDialog";
import { money, capCycle } from "@/components/super-admin/billingFormat";
import PurchaseBreakdown from "@/components/super-admin/PurchaseBreakdown";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// `money` and `capCycle` now live in components/super-admin/billingFormat and
// are imported above — the extracted provisioning dialog renders currency too,
// and a second copy would be a second place for the rounding rules to drift.

const PURCHASE_CHIP = {
  seat_addon: { label: "Seats", cls: "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300" },
  plan_upgrade: { label: "Plan upgrade", cls: "bg-violet-50 text-violet-700 dark:bg-violet-900/20 dark:text-violet-300" },
  cycle_switch: { label: "Cycle switch", cls: "bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300" },
};

// Payments carry a purpose CATEGORY (from constants/paymentPurpose.js) rather
// than a purchase kind, so they get their own chip lookup keyed by that.
const PAYMENT_CHIP = {
  signup: { label: "Signup", cls: "bg-teal-50 text-teal-700 dark:bg-teal-900/20 dark:text-teal-300" },
  renewal: { label: "Renewal", cls: "bg-indigo-50 text-indigo-700 dark:bg-indigo-900/20 dark:text-indigo-300" },
  trial_to_paid: { label: "Trial conversion", cls: "bg-cyan-50 text-cyan-700 dark:bg-cyan-900/20 dark:text-cyan-300" },
  mandate_migration: { label: "Mandate migration", cls: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300" },
  /**
   * These three reach the table as offline charges — either recorded by hand,
   * or raised automatically by a super-admin provision. Without an entry each
   * would fall through to the generic "Payment" chip and lose what it was for.
   *
   * Aliased to the matching PURCHASE_CHIP entries rather than restated, so a
   * seat add-on reads identically whether it was bought online or invoiced.
   * (They WERE restated, inline and identically, a few lines above — the later
   * keys silently won. Same values, so nothing rendered differently, but the
   * duplicate pair was unreachable and the two comments disagreed about why
   * they existed.)
   */
  seat_addon: PURCHASE_CHIP.seat_addon,
  plan_upgrade: PURCHASE_CHIP.plan_upgrade,
  cycle_switch: PURCHASE_CHIP.cycle_switch,
  unknown: { label: "Payment", cls: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300" },
};

/**
 * Purchase rows arrive with two different status vocabularies — request rows use
 * applied / pending_payment / failed, Payment rows use paid / created / failed —
 * and the filter offers only the first set. Group them so a signup charge is not
 * invisible under "Successful".
 */
const PURCHASE_STATUS_BUCKET = {
  applied: "applied",
  paid: "applied",
  pending_payment: "pending_payment",
  created: "pending_payment",
  failed: "failed",
  refunded: "failed",
};

// Map a subscription_timeline event to its icon, colour and copy. Keeps the
// timeline JSX itself dumb.
const timelineMeta = (ev) => {
  const seats = (n) => (n === 1 ? "1 seat" : `${n} seats`);
  const cap = (c) => (c ? c.charAt(0).toUpperCase() + c.slice(1) : "");
  switch (ev.type) {
    case "subscription_started":
      return {
        icon: <Package className="h-3.5 w-3.5" />,
        tone: "bg-primary/10 text-primary",
        title: `Started ${ev.plan_name || "plan"}${ev.billing_cycle ? ` · ${cap(ev.billing_cycle)}` : ""}`,
        detail: `${seats(ev.seats ?? 0)} · ${ev.is_online ? "Online (Razorpay)" : "Manual billing"}`,
      };
    case "subscription_status":
      return {
        icon: <X className="h-3.5 w-3.5" />,
        tone: "bg-rose-50 text-rose-600 dark:bg-rose-900/30 dark:text-rose-300",
        title: `Subscription ${String(ev.status || "").replace("_", " ")}`,
        detail: ev.plan_name || null,
      };
    case "employee_addon":
      return {
        icon: <Users className="h-3.5 w-3.5" />,
        tone: "bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-300",
        title: `Added ${seats(ev.extra_employees ?? 0)}`,
        detail: `${ev.seats_before} → ${ev.seats_after} licensed seats (prorated)`,
      };
    case "plan_upgrade": {
      // A cycle switch (e.g. monthly → annual) — the plan name may or may not
      // also change.
      if (ev.cycle_switch) {
        const planChanged = ev.from_plan_name !== ev.to_plan_name;
        return {
          icon: <TrendingUp className="h-3.5 w-3.5" />,
          tone: "bg-violet-50 text-violet-600 dark:bg-violet-900/30 dark:text-violet-300",
          title: `Switched to ${cap(ev.billing_cycle || "annual")} billing${
            planChanged ? `: ${ev.from_plan_name} → ${ev.to_plan_name}` : ` · ${ev.to_plan_name}`
          }`,
          detail: `${seats(ev.seats ?? 0)}${
            ev.from_billing_cycle
              ? ` · ${cap(ev.from_billing_cycle)} → ${cap(ev.billing_cycle)}`
              : ""
          } · full term paid online`,
        };
      }
      return {
        icon: <TrendingUp className="h-3.5 w-3.5" />,
        tone: "bg-violet-50 text-violet-600 dark:bg-violet-900/30 dark:text-violet-300",
        title: `Plan upgraded: ${ev.from_plan_name} → ${ev.to_plan_name}${
          ev.billing_cycle ? ` · ${cap(ev.billing_cycle)}` : ""
        }`,
        detail: `${seats(ev.seats ?? 0)}${ev.no_charge ? " · no charge (₹0 prorated difference)" : " · prorated difference paid online"}`,
      };
    }
    case "request_resolved": {
      const approved = ev.status === "approved";
      const isPlan = ev.request_type === "plan_upgrade";
      // A price change moves no seats and no plan, so the seat wording read
      // "seat request: 5 → 5" — a row that recorded nothing that happened.
      const isPrice = ev.request_type === "custom_price";
      return {
        icon: approved ? <Check className="h-3.5 w-3.5" /> : <X className="h-3.5 w-3.5" />,
        tone: approved
          ? "bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-300"
          : "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400",
        title: isPrice
          ? `${approved ? "Applied" : "Rejected"} custom rate${
              ev.custom_price_per_user != null
                ? `: ${money(ev.custom_price_per_user)}/user`
                : ""
            }`
          : isPlan
            ? `${approved ? "Approved" : "Rejected"} plan upgrade: ${ev.from_plan_name || "current"} → ${ev.to_plan_name}`
            : `${approved ? "Approved" : "Rejected"} seat request: ${ev.seats_before} → ${ev.seats_after}`,
        detail: isPrice
          ? `Same plan and seats${ev.seats_after != null ? ` (${ev.seats_after})` : ""} · super-admin decision`
          : `Super-admin decision${ev.requested_by ? ` · requested by ${ev.requested_by}` : ""}`,
      };
    }
    case "payment":
      return {
        icon: <Receipt className="h-3.5 w-3.5" />,
        tone: "bg-amber-50 text-amber-600 dark:bg-amber-900/30 dark:text-amber-300",
        title: `Payment received · ${String(ev.purpose || "subscription").replace(/_/g, " ")}`,
        detail: null,
      };
    default:
      return {
        icon: <History className="h-3.5 w-3.5" />,
        tone: "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400",
        title: ev.type,
        detail: null,
      };
  }
};

// Colour a status/lifecycle label consistently across the tables.
const STATUS_TONE = {
  active: "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-300 dark:border-emerald-700/40",
  applied: "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-300 dark:border-emerald-700/40",
  paid: "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-300 dark:border-emerald-700/40",
  trial: "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/20 dark:text-blue-300 dark:border-blue-700/40",
  scheduled: "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/20 dark:text-blue-300 dark:border-blue-700/40",
  created: "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-300 dark:border-amber-700/40",
  pending_payment: "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-300 dark:border-amber-700/40",
  paused: "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-300 dark:border-amber-700/40",
  past_due: "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-300 dark:border-amber-700/40",
  expired: "bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-900/20 dark:text-rose-300 dark:border-rose-700/40",
  // A real CompanySubscription status that had no tone — it now reaches the
  // card (ended subscriptions are shown), so give it the ended colour instead
  // of the neutral fallback.
  trial_expired: "bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-900/20 dark:text-rose-300 dark:border-rose-700/40",
  cancelled: "bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-900/20 dark:text-rose-300 dark:border-rose-700/40",
  failed: "bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-900/20 dark:text-rose-300 dark:border-rose-700/40",
  suspended: "bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-900/20 dark:text-rose-300 dark:border-rose-700/40",
};

const StatusPill = ({ status }) => {
  const key = (status || "").toLowerCase();
  const tone =
    STATUS_TONE[key] ||
    "bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700";
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold capitalize ${tone}`}
    >
      {(status || "—").toString().replace(/_/g, " ")}
    </span>
  );
};

/**
 * The Plan & Subscription fact table.
 *
 * Extracted so the Current tab and the Previous-subscriptions tab render a past
 * term through exactly the same rows — a second hand-rolled table for history
 * would drift from this one the moment either side gains a field.
 *
 * `historical` = this record is not the live term. It only changes how dates are
 * labelled: a past subscription keeps a stale `next_billing_date`, and showing
 * that as "Renews on" would assert a renewal that will never happen.
 */
const SubscriptionFacts = ({ sub, historical }) => (
  <div className="overflow-x-auto rounded-2xl border border-slate-100 dark:border-slate-800">
    <Table>
      <TableBody>
        {[
          ["Plan", sub.plan_name || "—"],
          ["Plan ID", sub.plan_id || "—"],
          ["Status", <StatusPill status={sub.status} />],
          [
            "Billing cycle",
            <span className="capitalize">{sub.billing_cycle || "—"}</span>,
          ],
          ["Seats (paid employees)", sub.number_of_employees ?? "—"],
          [
            "Price / user",
            sub.billing_cycle === "annual"
              ? `${sub.currency || "INR"} ${sub.price_per_user_annual}/yr`
              : `${sub.currency || "INR"} ${sub.price_per_user_monthly}/mo`,
          ],
          [
            // Spelt out because "Amount" alone read ambiguously next to
            // "Price / user" — this is the recurring total for the cycle,
            // before GST.
            sub.billing_cycle === "annual"
              ? "Amount / year (excluding GST)"
              : "Amount / month (excluding GST)",
            money(sub.amount, sub.currency),
          ],
          [
            "Payment mode",
            sub.isPaidOffline ? "Online (Razorpay)" : "Manual / Offline",
          ],
          ["Custom pricing", sub.is_custom_course ? "Yes" : "No"],
          ["Start date", sub.start_date ? formatDate(sub.start_date) : "—"],
          /**
           * An auto-renewing Razorpay subscription has no `end_date` by design
           * — the term rolls at `next_billing_date`. The card previously showed
           * only End date, so an active annual plan displayed "—" and the cycle
           * end was invisible. Show the renewal date for the live term and fall
           * back to End date for anything historical.
           */
          ...(!historical && sub.next_billing_date
            ? [[
                sub.auto_renew === false ? "Cycle ends" : "Renews on",
                formatDate(sub.next_billing_date),
              ]]
            : [[
                "End date",
                sub.end_date ? formatDate(sub.end_date) : "—",
              ]]),
          /**
           * Only meaningful once the term has actually ended: until then "when
           * access is cut" is a date nobody needs. After it, this is the one
           * date that matters — the end date is not when the company stops
           * working, `grace_days` later is.
           */
          ...(sub.access_until && new Date(sub.end_date) < new Date()
            ? [[
                `Access until (${sub.grace_days ?? ""}-day grace)`,
                formatDate(sub.access_until),
              ]]
            : []),
          ...(sub.last_charged_at
            ? [["Last charged", formatDate(sub.last_charged_at)]]
            : []),
          // Only meaningful on a trial — it is null on every paid subscription
          // and was just a permanent "—".
          ...(sub.trial_end_date
            ? [["Trial ends", formatDate(sub.trial_end_date)]]
            : []),
        ].map(([label, value]) => (
          <TableRow key={label}>
            <TableCell className="w-1/2 font-medium text-slate-600 dark:text-slate-300">
              {label}
            </TableCell>
            <TableCell className="font-semibold text-slate-900 dark:text-foreground">
              {value}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </div>
);

/** One-line summary of a past term, used as the Previous tab's list rows. */
const PreviousSubscriptionRow = ({ sub, expanded, onToggle }) => (
  <div className="rounded-xl border border-slate-200/70 dark:border-slate-700 overflow-hidden">
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={expanded}
      className="w-full flex flex-wrap items-center gap-x-4 gap-y-1.5 px-3.5 py-3 text-left hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
    >
      <span className="text-xs font-semibold capitalize text-slate-900 dark:text-foreground">
        {sub.plan_name || sub.plan_id || "—"}
        <span className="ml-1.5 font-normal capitalize text-slate-400">
          · {sub.billing_cycle || "—"}
        </span>
      </span>
      <span className="text-xs text-slate-500 dark:text-slate-400">
        {sub.start_date ? formatDate(sub.start_date) : "—"}
        {" → "}
        {sub.end_date ? formatDate(sub.end_date) : "—"}
      </span>
      <span className="text-xs text-slate-500 dark:text-slate-400">
        {sub.number_of_employees ?? "—"} seats ·{" "}
        {money(sub.amount, sub.currency)}
      </span>
      <span className="ml-auto flex items-center gap-2">
        <StatusPill status={sub.status} />
        <ChevronDown
          className={`h-4 w-4 text-slate-400 transition-transform ${expanded ? "rotate-180" : ""}`}
        />
      </span>
    </button>
    {expanded && (
      <div className="border-t border-slate-200/70 dark:border-slate-700 p-3">
        <SubscriptionFacts sub={sub} historical />
      </div>
    )}
  </div>
);

const CompanyDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const companyId = location.state?.companyId || null;
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [resendingInvite, setResendingInvite] = useState(false);

  const handleResendAdminInvite = async () => {
    if (!companyId) return;
    setResendingInvite(true);
    try {
      await resendCompanyAdminInvite(companyId);
      toast.success("Invitation re-sent to the company admin.");
    } catch (err) {
      toast.error(err?.response?.data?.error || "Could not resend the invitation.");
    } finally {
      setResendingInvite(false);
    }
  };

  // Seat-request resolution
  const [resolveTarget, setResolveTarget] = useState(null); // the request being resolved
  const [approveSeats, setApproveSeats] = useState(1);
  const [resolveNote, setResolveNote] = useState("");
  const [resolving, setResolving] = useState(false);

  // Subscription timeline — one row per event, so a long-running company pushes
  // Purchase History off-screen. Show a page at a time and let them ask for more.
  const TIMELINE_PAGE_SIZE = 10;
  const [timelineShown, setTimelineShown] = useState(TIMELINE_PAGE_SIZE);
  const timelineEvents = Array.isArray(company?.subscription_timeline)
    ? company.subscription_timeline
    : [];
  const visibleTimeline = timelineEvents.slice(0, timelineShown);
  const timelineRemaining = timelineEvents.length - visibleTimeline.length;

  // Purchase History pagination (seat add-ons + plan upgrades + cycle switches).
  const PURCHASES_PER_PAGE = 8;
  const [purchasesPage, setPurchasesPage] = useState(1);
  const [purchasesStatusFilter, setPurchasesStatusFilter] = useState('all');
  // Which row has its invoice breakdown open. One at a time — the strip is
  // wide, and several open at once turns the table back into a wall of numbers.
  const [openPurchase, setOpenPurchase] = useState(null);

  const purchaseRows = useMemo(() => {
    return [
      ...(Array.isArray(company?.employee_addons) ? company.employee_addons : []),
      ...(Array.isArray(company?.plan_upgrades) ? company.plan_upgrades : []),
      // Signup / renewal / trial-conversion charges. Without these the table
      // read "No purchases yet" for any company that had never bought a
      // mid-cycle add-on — including every company that had simply paid.
      ...(Array.isArray(company?.payments) ? company.payments : []),
    ].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
  }, [company]);

  const filteredPurchases = purchasesStatusFilter === 'all'
    ? purchaseRows
    : purchaseRows.filter(
        (p) => (PURCHASE_STATUS_BUCKET[p.status] || p.status) === purchasesStatusFilter
      );

  const purchaseTotalPages = Math.max(1, Math.ceil(filteredPurchases.length / PURCHASES_PER_PAGE));
  const pagedPurchases = filteredPurchases.slice(
    (purchasesPage - 1) * PURCHASES_PER_PAGE,
    purchasesPage * PURCHASES_PER_PAGE
  );

  // Clamp the page if the data shrank (e.g. after a refetch).
  useEffect(() => {
    if (purchasesPage > purchaseTotalPages) setPurchasesPage(purchaseTotalPages);
  }, [purchaseTotalPages, purchasesPage]);

  // ── Direct provisioning (offline-billed companies only) ─────────────────
  // Online companies are governed by their Razorpay mandate; granting seats
  // here would license seats the mandate does not bill for. The backend
  // enforces this too — the UI just doesn't offer it.
  //
  /**
   * What the Plan & Subscription card displays.
   *
   * `company.subscription` is only populated while the term is active/paused,
   * so a cancelled, expired, trial-expired or past-due company used to show a
   * bare "no subscription" box — losing the plan, the price and the end date
   * right when support needs them. Fall back to `latest_subscription` (the most
   * recent record of any status) and render it as history.
   *
   * Display only. Every mutating control — provisioning, seats, plan change —
   * stays bound to `company.subscription` above, so nothing becomes editable
   * just because an ended subscription is now visible.
   */
  const displaySubscription =
    company?.subscription || company?.latest_subscription || null;
  const subscriptionIsHistorical =
    !!displaySubscription && displaySubscription.is_current === false;

  // Quick Stats measures the roster against what is actually paid for, so a
  // company sitting over its seat count shows up without opening the
  // subscription card. `employee_count` is the Employee collection (the HR
  // roster); `user_count` is the User collection (accounts that can sign in).
  const employeeCount = Number(company?.statistics?.employee_count) || 0;
  const userCount = Number(company?.statistics?.user_count) || 0;
  const paidSeats = Number(displaySubscription?.number_of_employees);
  const hasSeatCap = Number.isFinite(paidSeats) && paidSeats > 0;
  const seatsSpare = hasSeatCap ? paidSeats - employeeCount : null;
  const seatsUsedPct = hasSeatCap
    ? Math.min(100, Math.round((employeeCount / paidSeats) * 100))
    : 0;

  /**
   * Everything the card is not showing in the Current tab. The backend already
   * excludes the displayed record, so a subscription never appears twice.
   */
  const previousSubscriptions = Array.isArray(company?.previous_subscriptions)
    ? company.previous_subscriptions
    : [];
  const [subTab, setSubTab] = useState("current");
  const [expandedPrevSub, setExpandedPrevSub] = useState(null);
  // The Previous trigger is not rendered when there is no history, so pin the
  // view back to Current if a refetch empties the list — otherwise the card
  // would sit on a tab whose content and trigger have both disappeared.
  const activeSubTab = previousSubscriptions.length ? subTab : "current";
  /**
   * Direct provisioning — state, derivation and submit all live in the hook,
   * and the dialog is rendered from it at the bottom of this file. Extracted so
   * EditCompany can drive the same flow instead of forking it.
   *
   * onDone is wrapped rather than passed by reference: fetchCompanyDetails is a
   * const declared further down, so naming it here directly would hit its
   * temporal dead zone on first render.
   */
  const provision = useProvisionCompany({
    company,
    companyId,
    onDone: () => fetchCompanyDetails(),
  });
  const { provisionTarget, isPaidOnline, canProvision, openProvision } = provision;

  /**
   * ── Record an offline purchase ──────────────────────────────────────────
   *
   * An offline-billed company generates no Razorpay charge, so nothing writes
   * it a Purchase History row. This is the manual entry point: the super-admin
   * supplies only the date the invoice was settled and, if it differs from what
   * the plan bills, the amount. Plan, cycle and seats are NOT retyped — they
   * come off the subscription on the server, so a typo here cannot file a
   * purchase against a plan the company was never on.
   */
  const [purchaseOpen, setPurchaseOpen] = useState(false);
  const [purchaseType, setPurchaseType] = useState("renewal");
  const [purchaseDate, setPurchaseDate] = useState("");
  const [purchaseAmount, setPurchaseAmount] = useState("");
  const [purchaseNote, setPurchaseNote] = useState("");
  const [savingPurchase, setSavingPurchase] = useState(false);

  /**
   * What the subscription bills today, GST-inclusive — the figure the dialog
   * pre-fills and the server defaults to.
   *
   * Derived from rate × seats rather than read from `sub.amount`: that field is
   * denormalised by a pre-save hook, so any `updateOne` path leaves it stale
   * (the same reason provisionPreview recomputes it). The 18% mirrors
   * calculateSubscriptionGST — Maharashtra splits it CGST+SGST 9/9, everywhere
   * else is a single IGST 18, and the total is identical either way.
   */
  const purchaseQuote = useMemo(() => {
    const sub = provisionTarget;
    // Unreachable while the button is gated on `canProvision` (which requires a
    // subscription), but the dialog reads every field — so it gets a whole
    // object rather than a partial one that renders "undefined".
    if (!sub) {
      return {
        cycle: "monthly",
        seats: 0,
        perUser: null,
        plan_name: "—",
        currency: "INR",
        gross: null,
      };
    }
    const cycle = sub.billing_cycle === "annual" ? "annual" : "monthly";
    const perUser = Number(
      cycle === "annual" ? sub.price_per_user_annual : sub.price_per_user_monthly
    );
    const seats = Number(sub.number_of_employees ?? 0);
    const base =
      Number.isFinite(perUser) && perUser > 0 && seats > 0
        ? +(perUser * seats).toFixed(2)
        : null;
    return {
      cycle,
      seats,
      perUser: Number.isFinite(perUser) ? perUser : null,
      plan_name: sub.plan_name || sub.plan_id || "—",
      currency: sub.currency || "INR",
      gross: base != null ? +(base * 1.18).toFixed(2) : null,
    };
  }, [provisionTarget]);

  const openAddPurchase = () => {
    // Today, as YYYY-MM-DD in LOCAL time. toISOString() would convert to UTC
    // first, which in IST hands back yesterday's date for most of the evening.
    const now = new Date();
    const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
    setPurchaseType("renewal");
    setPurchaseDate(local.toISOString().slice(0, 10));
    setPurchaseAmount(purchaseQuote.gross != null ? String(purchaseQuote.gross) : "");
    setPurchaseNote("");
    setPurchaseOpen(true);
  };

  /**
   * Switching type re-decides the amount.
   *
   * A renewal or a signup charges the whole subscription, so the quote is the
   * right pre-fill. A seat add-on or a mid-term plan upgrade charges a PART of
   * it — pre-filling the full bill there would invite a wrong figure through by
   * simply not being edited, so the field is cleared and has to be typed.
   */
  const changePurchaseType = (next) => {
    setPurchaseType(next);
    const billsFullTerm = next === "renewal" || next === "signup";
    setPurchaseAmount(
      billsFullTerm && purchaseQuote.gross != null ? String(purchaseQuote.gross) : ""
    );
  };

  /**
   * Settling a pending offline invoice — the money arrived outside the platform.
   *
   * Only ever reaches a row the server flagged `settleable` (an offline invoice
   * still in "created"); a gateway charge's status belongs to Razorpay and the
   * endpoint refuses one outright.
   */
  const [settleTarget, setSettleTarget] = useState(null);
  const [settleDate, setSettleDate] = useState("");
  const [settling, setSettling] = useState(false);

  const openSettle = (payment) => {
    setSettleTarget(payment);
    // Default to today — the common case is recording a payment as it lands.
    setSettleDate(new Date().toISOString().slice(0, 10));
  };

  const submitSettle = async () => {
    if (settling || !settleTarget) return;
    if (!settleDate) {
      toast.error("Pick the date the payment was received.");
      return;
    }
    setSettling(true);
    try {
      const res = await settleOfflinePayment(companyId, settleTarget._id, settleDate);
      toast.success(res?.message || "Payment marked as paid.");
      setSettleTarget(null);
      await fetchCompanyDetails();
    } catch (err) {
      toast.error(
        err?.response?.data?.error || "Could not settle this payment. Please try again."
      );
    } finally {
      setSettling(false);
    }
  };

  const submitPurchase = async () => {
    if (savingPurchase) return;
    setSavingPurchase(true);
    try {
      const res = await addCompanyPurchase(companyId, {
        paid_on: purchaseDate,
        purchase_type: purchaseType,
        // Only sent when it differs from the quote — otherwise let the server
        // compute it, so the row is priced by the same code as every other
        // charge rather than by whatever this page rounded to.
        ...(purchaseAmount !== "" && Number(purchaseAmount) !== purchaseQuote.gross
          ? { amount: Number(purchaseAmount) }
          : {}),
        ...(purchaseNote.trim() ? { note: purchaseNote.trim() } : {}),
      });
      toast.success(res?.message || "Purchase recorded.");
      setPurchaseOpen(false);
      await fetchCompanyDetails();
    } catch (err) {
      toast.error(
        err?.response?.data?.error || "Could not record this purchase. Please try again."
      );
    } finally {
      setSavingPurchase(false);
    }
  };

  /**
   * ── Billing trace (operational diagnostics) ─────────────────────────────
   *
   * NOTHING BELOW IS WIRED UP. `loadTrace` is never called, `setTraceOpen` is
   * never called, and `trace` is never rendered — the state, the loader and the
   * `getCompanyBillingTrace` import are all here, but no UI was ever added to
   * trigger them.
   *
   * The backend half is complete and live: GET
   * /super-admin/companies/:companyId/billing-trace, backed by
   * billingTrace.controller.js, including the `?reconcile=true` diff of local
   * state against Razorpay. So this is a stranded feature, not dead code —
   * deleting it would remove the only trace of the intent, and wiring it up is
   * a real piece of work rather than a cleanup. Left as-is deliberately.
   */
  // Loaded on demand rather than with the page: it is a separate query and
  // most visits to this page never need it.
  const [trace, setTrace] = useState(null);
  const [traceLoading, setTraceLoading] = useState(false);
  const [traceOpen, setTraceOpen] = useState(false);
  const [traceExpanded, setTraceExpanded] = useState({});
  const [reconciling, setReconciling] = useState(false);

  const loadTrace = async ({ reconcile = false } = {}) => {
    if (!companyId) return;
    reconcile ? setReconciling(true) : setTraceLoading(true);
    try {
      const res = await getCompanyBillingTrace(companyId, {
        limit: 300,
        ...(reconcile ? { reconcile: true } : {}),
      });
      setTrace(res);
      if (reconcile) {
        const drifted = res?.reconciliation?.drifted ?? 0;
        drifted > 0
          ? toast.warning(`${drifted} subscription(s) disagree with Razorpay.`)
          : toast.success("Local state matches Razorpay.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.error || "Could not load the billing trace.");
    } finally {
      setReconciling(false);
      setTraceLoading(false);
    }
  };

  const openResolve = (request) => {
    setResolveTarget(request);
    setApproveSeats(Number(request?.requested_seats) || 1);
    setResolveNote("");
  };

  const submitResolve = async (action) => {
    if (!resolveTarget) return;
    setResolving(true);
    try {
      const payload = {
        requestId: resolveTarget._id,
        action,
        note: resolveNote.trim() || undefined,
      };
      if (action === "approve") payload.approved_seats = Number(approveSeats);
      const res = await resolveSeatRequest(payload);
      toast.success(
        res?.data?.message ||
          res?.message ||
          (action === "approve" ? "Seat request approved." : "Seat request rejected.")
      );
      setResolveTarget(null);
      fetchCompanyDetails();
    } catch (err) {
      toast.error(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          "Failed to resolve seat request."
      );
    } finally {
      setResolving(false);
    }
  };

  useEffect(() => {
    fetchCompanyDetails();
  }, [companyId]);

  const fetchCompanyDetails = async () => {
    if (!companyId) {
      setError("No company ID provided.");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await getCompanyById(companyId);
      console.log("COMPANY:  ", response);
      setCompany(response);
    } catch (error) {
      console.error("Error fetching company details:", error);
      setError(
        error.response?.data?.detail || "Failed to load company details"
      );
      toast.error("Failed to load company details");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto">
        <Card>
          <CardContent className="p-12 text-center">
            <AlertCircle className="h-16 w-16 mx-auto text-red-500 mb-4" />
            <h3 className="text-xl font-bold text-foreground mb-2">
              Error Loading Company
            </h3>
            <p className="text-muted-foreground mb-6">{error}</p>
            <Button
              onClick={() => navigate("/super-admin/companies")}
              variant="outline"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Companies
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto space-y-8">

      {displaySubscription?.end_date && (() => {
        const endDate = new Date(displaySubscription.end_date);
        const now = new Date();
        const hoursLeft = (endDate - now) / (1000 * 60 * 60);

        const isExpired = hoursLeft < 0;
        const isExpiringSoon = hoursLeft >= 0 && hoursLeft <= 48;

        if (!isExpired && !isExpiringSoon) return null;

        /**
         * The term ending and the company losing access are not the same day —
         * expirePaidSubscriptions keeps it running for a grace window first.
         * Both values come from the API (constants/billing.js) so this banner
         * can never promise a window the cron does not honour.
         */
        const accessUntil = displaySubscription.access_until
          ? new Date(displaySubscription.access_until)
          : null;
        const graceDays = displaySubscription.grace_days ?? null;
        const inGrace = isExpired && accessUntil && accessUntil > now;
        const graceDaysLeft = inGrace
          ? Math.max(1, Math.ceil((accessUntil - now) / (1000 * 60 * 60 * 24)))
          : 0;

        return (
          <div className="mb-4">
            <div className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4
        rounded-lg border px-4 py-3 backdrop-blur-sm
        ${isExpired ? "border-red-400/40 bg-red-500/10 dark:bg-red-500/15"
                : "border-yellow-400/40 bg-yellow-500/10 dark:bg-yellow-500/15"
              }`}
            >
              {/* LEFT: MESSAGE */}
              <div className="flex gap-3">
                <div className="pt-0.5">
                  <AlertCircle className={`h-5 w-5 ${isExpired ? "text-red-500" : "text-yellow-500"}`} />
                </div>

                <div>
                  <h3 className={`text-sm font-semibold ${isExpired
                    ? "text-red-600 dark:text-red-400" : "text-yellow-700 dark:text-yellow-400"}`}>
                    {isExpired
                      ? inGrace
                        ? "Subscription Expired — in grace period"
                        : "Subscription Expired"
                      : "Subscription Ending Soon"}
                  </h3>

                  <p className={`mt-1 text-sm leading-relaxed ${isExpired
                    ? "text-red-600/90 dark:text-red-300" : "text-yellow-700/90 dark:text-yellow-300"
                    }`}
                  >
                    The subscription for this company{" "}
                    {isExpired ? "expired on" : "will expire on"}{" "}
                    <span className="font-medium">
                      {formatDate(endDate)}
                    </span>
                    {/* "Immediate action is required" was misleading on day one
                        of the grace window — the company is still working. Say
                        what actually happens, and when. */}
                    {!isExpired
                      ? ". Please renew to avoid interruption."
                      : inGrace
                        ? `. It keeps working for ${graceDays ? `${graceDays} ` : ""}grace ${graceDays === 1 ? "day" : "days"} — access is cut on `
                        : accessUntil
                          ? `. The ${graceDays ?? ""}-day grace period ended on `
                          : ". Immediate action is required."}
                    {isExpired && accessUntil && (
                      <>
                        <span className="font-medium">{formatDate(accessUntil)}</span>
                        {inGrace
                          ? ` (${graceDaysLeft} ${graceDaysLeft === 1 ? "day" : "days"} left).`
                          : " and employees can no longer sign in."}
                      </>
                    )}
                  </p>
                </div>
              </div>

              {/* RIGHT: ACTION — offline companies renew here (this folds the old
                  "Mark as Paid" step into the one provisioning dialog, so a
                  renewal can also adjust seats/plan/rate). Online companies renew
                  through Razorpay, so no manual button is offered for them. */}
              {canProvision && (
                <button
                  onClick={() => openProvision({ renew: true })}
                  className="group inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-emerald-700/30
                    bg-gradient-to-b from-emerald-500 to-emerald-600 px-4 py-2
                    text-xs font-semibold text-white shadow-sm shadow-emerald-900/20
                    transition-all duration-200
                    hover:-translate-y-0.5 hover:from-emerald-400 hover:to-emerald-500 hover:shadow-md hover:shadow-emerald-900/25
                    active:translate-y-0 active:shadow-sm
                    focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-900"
                >
                  <CheckCircle2 className="h-3.5 w-3.5 transition-transform duration-200 group-hover:scale-110" />
                  Renew &amp; mark paid
                </button>
              )}
          </div>
          </div>
        );
      })()}

      <div className="relative overflow-hidden rounded-2xl border border-slate-200/70 dark:border-slate-700 bg-card dark:bg-slate-900 shadow-sm">
        {/* subtle top glow */}

        <div className="pointer-events-none absolute -top-24 right-0 h-56 w-56 rounded-full bg-primary/10 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-28 left-0 h-64 w-64 rounded-full bg-slate-200/40 dark:bg-slate-700/30 blur-3xl" />

        <div className="relative p-4 sm:p-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            {/* Left: Back + Title */}
            <div className="flex items-start gap-4">
              <Button
                variant="outline"
                onClick={() => navigate("/super-admin/companies")}
                className="h-10 px-3 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>

              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-3">
                  {/* Logo (only if exists) */}
                  {company?.company?.logo && (
                    <div className="shrink-0">
                      <div className="h-11 w-11 rounded-full border border-slate-200 dark:border-slate-700 bg-card dark:bg-slate-900 shadow-sm overflow-hidden flex items-center justify-center">
                        <img
                          src={company.company.logo}
                          alt={`${company.company.name || "Company"} logo`}
                          className="h-full w-full object-cover"
                          onError={(e) => {
                            // If image broken, hide the whole circle
                            e.currentTarget.closest("div")?.remove();
                          }}
                        />
                      </div>
                    </div>
                  )}

                  <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-slate-900 dark:text-foreground truncate">
                    {company?.company?.company_name || "Company"}
                  </h1>

                  {/* Status badge */}
                  <span
                    className={[
                      "inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold border shadow-sm",
                      company?.company?.status === "active"
                        ? "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:border-emerald-500/20"
                        : "bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:border-slate-700",
                    ].join(" ")}
                  >
                    <span
                      className={[
                        "h-2 w-2 rounded-full",
                        company?.company?.status === "active"
                          ? "bg-emerald-500"
                          : "bg-slate-400",
                      ].join(" ")}
                    />
                    {company?.company?.status === "active" ? "Active" : "Inactive"}
                  </span>
                </div>

                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                  Company details overview
                </p>
              </div>

            </div>

            {/* Right: Actions */}
            <div className="flex gap-2 sm:justify-end">
              
              <Button
                onClick={() => {
                  navigate("/super-admin/companies/edit", {
                    state: { companyId },
                  });
                }}
                className="h-10 px-4 bg-primary text-white hover:bg-primary/90 shadow-sm"
              >
                <Edit className="h-4 w-4 mr-2" />
                Edit Company
              </Button>
            </div>
          </div>
        </div>
      </div>

      <hr className="border-t border-border" />
      {/* Company Information */}
      <div className="flex flex-col lg:flex-row gap-4">
        {/* LEFT (70%) */}
        <div className="lg:basis-[70%] lg:max-w-[70%]">
          {/* Essential Information */}
          <Card className="h-full relative overflow-hidden rounded-2xl border border-slate-200/70 dark:border-slate-700 bg-card dark:bg-slate-900 shadow-sm">
            <div className="pointer-events-none absolute -top-24 -right-20 h-56 w-56 rounded-full bg-primary/10 blur-3xl" />

            <CardHeader className="py-4">
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-xl bg-primary/10 border border-primary/15 flex items-center justify-center">
                    <Building2 className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm sm:text-base font-bold text-slate-900 dark:text-foreground">
                      Essential Information
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      Key company details at a glance
                    </p>
                  </div>
                </div>

                <span className="hidden sm:inline-flex items-center gap-2 rounded-full border border-slate-200 dark:border-slate-700 px-3 py-1 text-[11px] font-semibold text-slate-700 dark:text-slate-200 bg-slate-50 dark:bg-slate-800">
                  <span className="h-2 w-2 rounded-full bg-primary" />
                  Profile
                </span>
              </CardTitle>
            </CardHeader>

            <CardContent className="pt-0 pb-4">
              <div className="rounded-2xl border border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/30 p-4">
                {/* 2 columns on desktop to keep it compact */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                      Name
                    </p>
                    <p className="mt-1 text-base font-bold text-slate-900 dark:text-foreground truncate">
                      {company?.company?.company_name || "-"}
                    </p>
                  </div>

                  <div className="min-w-0">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                      Contact Email
                    </p>
                    <div className="mt-1 flex items-center gap-2 min-w-0">
                      <span className="h-8 w-8 rounded-lg border border-slate-200/70 dark:border-slate-700 bg-card dark:bg-slate-900 flex items-center justify-center shrink-0">
                        <Mail className="h-4 w-4 text-primary" />
                      </span>
                      <span className="text-slate-900 dark:text-foreground font-semibold truncate">
                        {company?.company?.contact_email || "-"}
                      </span>
                    </div>
                  </div>

                  {company?.company?.phone && (
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                        Phone
                      </p>
                      <div className="mt-1 flex items-center gap-2">
                        <span className="h-8 w-8 rounded-lg border border-slate-200/70 dark:border-slate-700 bg-card dark:bg-slate-900 flex items-center justify-center shrink-0">
                          <Phone className="h-4 w-4 text-primary" />
                        </span>
                        <span className="text-slate-900 dark:text-foreground font-semibold">
                          {company.company.phone}
                        </span>
                      </div>
                    </div>
                  )}

                  {company?.company?.industry && (
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                        Industry
                      </p>
                      <p className="mt-1 text-slate-900 dark:text-foreground font-semibold capitalize truncate">
                        {company.company.industry}
                      </p>
                    </div>
                  )}

                  {/* Address spans full width */}
                  {company?.company?.address && (
                    <div className="md:col-span-2">
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                        Address
                      </p>
                      <div className="mt-1 flex items-start gap-2">
                        <span className="mt-0.5 h-8 w-8 rounded-lg border border-slate-200/70 dark:border-slate-700 bg-card dark:bg-slate-900 flex items-center justify-center shrink-0">
                          <MapPin className="h-4 w-4 text-primary" />
                        </span>
                        <span className="text-slate-900 dark:text-foreground font-semibold break-words">
                          {company.company.address}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* RIGHT (30%) */}
        <div className="lg:basis-[30%] lg:max-w-[30%]">
          {/* Administrator Details */}
          {company?.admin_user && (
            <Card className="h-full relative overflow-hidden rounded-2xl border border-slate-200/70 dark:border-slate-700 bg-card dark:bg-slate-900 shadow-sm flex flex-col">
              <div className="pointer-events-none absolute -top-24 -right-20 h-56 w-56 rounded-full bg-indigo-500/10 blur-3xl" />

              <CardHeader className="py-4">
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="h-10 w-10 rounded-xl bg-indigo-500/10 border border-indigo-200/40 dark:border-indigo-500/20 flex items-center justify-center shrink-0">
                      <User className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm sm:text-base font-bold text-slate-900 dark:text-foreground truncate">
                        Administrator
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        Primary admin contact
                      </p>
                    </div>
                  </div>

                  <span className="hidden sm:inline-flex items-center gap-2 rounded-full border border-slate-200 dark:border-slate-700 px-3 py-1 text-[11px] font-semibold text-slate-700 dark:text-slate-200 bg-slate-50 dark:bg-slate-800">
                    <span className="h-2 w-2 rounded-full bg-indigo-500" />
                    Admin
                  </span>
                </CardTitle>
              </CardHeader>

              <CardContent className="pt-0 pb-4 flex-1">
                <div className="h-full rounded-2xl border border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/30 p-4">
                  <div className="space-y-4">
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                        Admin Name
                      </p>
                      <p className="mt-1 text-base font-bold text-slate-900 dark:text-foreground truncate">
                        {company.admin_user.admin_name || "-"}
                      </p>
                    </div>

                    <div className="h-px bg-slate-200/70 dark:bg-slate-700/60" />

                    <div className="flex items-center gap-2 min-w-0">
                      <span className="h-8 w-8 rounded-lg border border-slate-200/70 dark:border-slate-700 bg-card dark:bg-slate-900 flex items-center justify-center shrink-0">
                        <Mail className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
                      </span>
                      <span className="text-sm font-semibold text-slate-900 dark:text-foreground truncate">
                        {company.admin_user.admin_email || "-"}
                      </span>
                    </div>

                    {company.admin_user.admin_phone && (
                      <div className="flex items-center gap-2">
                        <span className="h-8 w-8 rounded-lg border border-slate-200/70 dark:border-slate-700 bg-card dark:bg-slate-900 flex items-center justify-center shrink-0">
                          <Phone className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
                        </span>
                        <span className="text-sm font-semibold text-slate-900 dark:text-foreground">
                          {company.admin_user.admin_phone}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* No admin account yet — the owner hasn't accepted the invitation */}
          {!company?.admin_user && (
            <Card className="h-full rounded-2xl border border-slate-200/70 dark:border-slate-700 bg-card dark:bg-slate-900 shadow-sm">
              <CardHeader className="py-4">
                <CardTitle className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-xl bg-amber-500/10 border border-amber-200/40 dark:border-amber-500/20 flex items-center justify-center shrink-0">
                    <Mail className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm sm:text-base font-bold text-slate-900 dark:text-foreground">
                      Administrator
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      Invitation not accepted yet
                    </p>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0 pb-4 space-y-3">
                <p className="text-sm text-slate-600 dark:text-slate-300">
                  The company admin hasn't set up their account. Resending emails
                  them their invitation link again (renewed if it has expired).
                </p>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={handleResendAdminInvite}
                  disabled={resendingInvite}
                >
                  {resendingInvite ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Mail className="h-4 w-4 mr-2" />
                  )}
                  Resend invitation
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      <hr className="border-t border-border" />
      {/* Subscription */}
      <div className="flex flex-col lg:flex-row gap-4">
        {/* LEFT (70%) — Subscription */}
        {/* flex-col so the provisioning card below stacks instead of
            overflowing: the subscription card used to be h-full and claimed
            the whole row height, painting any sibling over the next section. */}
        <div className="lg:basis-[70%] lg:max-w-[70%] flex flex-col gap-4">
          {displaySubscription ? (
            <Card className="flex-1 border border-slate-200/70 dark:border-slate-700 bg-card dark:bg-slate-900 shadow-sm rounded-2xl overflow-hidden">
              <CardHeader className="py-4">
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div
                      className={`h-10 w-10 rounded-xl border flex items-center justify-center ${
                        subscriptionIsHistorical
                          ? "bg-slate-500/10 border-slate-400/20"
                          : "bg-primary/10 border-primary/15"
                      }`}
                    >
                      <FileText
                        className={`h-5 w-5 ${
                          subscriptionIsHistorical ? "text-slate-500 dark:text-slate-400" : "text-primary"
                        }`}
                      />
                    </div>
                    <div>
                      <p className="text-sm sm:text-base font-bold text-slate-900 dark:text-foreground">
                        Plan &amp; Subscription
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        {subscriptionIsHistorical
                          ? "Previous subscription — no plan is currently active"
                          : "Current active plan details"}
                      </p>
                    </div>
                  </div>
                  <StatusPill status={displaySubscription.status} />
                </CardTitle>
              </CardHeader>

              <CardContent className="pt-0 pb-4">
                <Tabs value={activeSubTab} onValueChange={setSubTab} className="w-full">
                  <TabsList className="flex justify-start space-x-6 overflow-x-auto bg-transparent h-auto p-0 mb-3 no-scrollbar">
                    <TabsTrigger value="current">
                      {subscriptionIsHistorical ? "Latest" : "Current"}
                    </TabsTrigger>
                    {/* Hidden entirely when there is no history — an empty tab
                        invites a click that leads nowhere. */}
                    {previousSubscriptions.length > 0 && (
                      <TabsTrigger value="previous">
                        Previous ({previousSubscriptions.length})
                      </TabsTrigger>
                    )}
                  </TabsList>

                  <TabsContent value="current" className="mt-0">
                    {/* The card no longer hides an ended subscription, so it
                        has to say plainly that what follows is history —
                        otherwise the same table reads as a live plan. */}
                    {subscriptionIsHistorical && (
                      <div className="mb-3 flex items-start gap-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 px-3 py-2.5">
                        <History className="h-4 w-4 mt-0.5 shrink-0 text-slate-500 dark:text-slate-400" />
                        <p className="text-xs text-slate-600 dark:text-slate-300">
                          This company has no active subscription. Showing its
                          most recent one
                          {displaySubscription.end_date
                            ? `, which ended on ${formatDate(displaySubscription.end_date)}`
                            : ""}
                          , for reference.
                          {previousSubscriptions.length > 0
                            ? " Older terms are under Previous."
                            : ""}
                        </p>
                      </div>
                    )}
                    <SubscriptionFacts
                      sub={displaySubscription}
                      historical={subscriptionIsHistorical}
                    />
                  </TabsContent>

                  {previousSubscriptions.length > 0 && (
                    <TabsContent value="previous" className="mt-0">
                      <p className="mb-3 text-xs text-slate-500 dark:text-slate-400">
                        Terms this company has held before the one above, newest
                        first. Read-only — expand a row for its full details.
                      </p>
                      <div className="flex flex-col gap-2">
                        {previousSubscriptions.map((s) => (
                          <PreviousSubscriptionRow
                            key={s._id}
                            sub={s}
                            expanded={expandedPrevSub === s._id}
                            onToggle={() =>
                              setExpandedPrevSub((cur) => (cur === s._id ? null : s._id))
                            }
                          />
                        ))}
                      </div>
                    </TabsContent>
                  )}
                </Tabs>
              </CardContent>
            </Card>
          ) : (
            <Card className="flex-1 border border-slate-200/70 dark:border-slate-700 bg-card dark:bg-slate-900 shadow-sm rounded-2xl">
              <CardContent className="p-8 text-center text-sm text-slate-500 dark:text-slate-400">
                {/* Reached only when the company has never had a subscription
                    at all — an ended one is shown above as history instead. */}
                This company has never had a subscription.
              </CardContent>
            </Card>
          )}

          {/* Direct provisioning — offline-billed companies only. An online
              company's seats follow its Razorpay mandate, so granting them
              here would license seats it isn't billed for. */}
          {canProvision && (
            <Card className="shrink-0 border border-slate-200/70 dark:border-slate-700 bg-card dark:bg-slate-900 shadow-sm rounded-2xl overflow-hidden">
              <CardHeader className="py-4">
                <CardTitle className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center">
                      <TrendingUp className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                    </div>
                    <div>
                      <p className="text-sm sm:text-base font-bold text-slate-900 dark:text-foreground">
                        Provision Directly
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        Billed manually — grant seats or change the plan without a request
                      </p>
                    </div>
                  </div>
                </CardTitle>
              </CardHeader>

              <CardContent className="pt-0 pb-4">
                <div className="rounded-2xl border border-amber-200/60 dark:border-amber-900/40 bg-amber-50/50 dark:bg-amber-950/20 p-4">
                  <p className="text-xs text-amber-800 dark:text-amber-300 mb-3">
                    Applies immediately and is recorded against this company's
                    request history. No payment is collected — bill the company
                    separately.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" onClick={() => openProvision()}>
                      <SlidersHorizontal className="h-3.5 w-3.5 mr-1.5" />
                      Manage subscription
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {company?.subscription && isPaidOnline && (
            <p className="shrink-0 text-xs text-slate-500 dark:text-slate-400">
              This company pays online via Razorpay. Seats and plan changes are
              driven by its mandate — it must purchase them from its own
              subscription page.
            </p>
          )}
        </div>

        {/* RIGHT (30%) — Stats (stacked, compact) */}
        <div className="lg:basis-[30%] lg:max-w-[30%]">
          <Card className="h-full border border-slate-200/70 dark:border-slate-700 bg-card dark:bg-slate-900 shadow-sm rounded-2xl overflow-hidden">
            <CardHeader className="py-4">
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-xl bg-primary/10 border border-primary/15 flex items-center justify-center">
                    <Users className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm sm:text-base font-bold text-slate-900 dark:text-foreground">
                      Quick Stats
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      Seats bought and how many are in use
                    </p>
                  </div>
                </div>
              </CardTitle>
            </CardHeader>

            <CardContent className="pt-0 pb-4">
              {/* One card, two rows: employees and users are easy to confuse, so
                  each says in a line what it counts and how it differs. */}
              <div className="rounded-2xl border border-slate-200/70 dark:border-slate-700 bg-card dark:bg-slate-900 divide-y divide-slate-200/70 dark:divide-slate-700 overflow-hidden">
                {[
                  {
                    label: hasSeatCap ? "Seats used" : "Employees",
                    value: employeeCount,
                    total: hasSeatCap ? paidSeats : null,
                    blurb: hasSeatCap
                      ? `Bought ${paidSeats} seat${paidSeats === 1 ? "" : "s"} · ${employeeCount} employee${employeeCount === 1 ? "" : "s"} added to the HR roster so far.`
                      : "Employees on this company's HR roster. No subscription, so there is nothing to measure them against yet.",
                    icon: <Users className="h-4 w-4 text-primary" />,
                    tile: "bg-primary/10 border-primary/15",
                  },
                  {
                    label: "Login accounts",
                    value: userCount,
                    blurb:
                      "People who can actually sign in — the company admin, plus every employee who has been given access.",
                    icon: (
                      <User className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                    ),
                    tile: "bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-700/40",
                  },
                ].map((stat) => (
                  <div key={stat.label} className="flex items-start gap-3 px-4 py-3.5">
                    <div
                      className={`h-9 w-9 rounded-xl border flex items-center justify-center shrink-0 ${stat.tile}`}
                    >
                      {stat.icon}
                    </div>

                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline gap-2">
                        <p className="text-2xl font-extrabold leading-none text-slate-900 dark:text-foreground">
                          {stat.value}
                          {stat.total !== null && stat.total !== undefined && (
                            <span className="ml-1 text-sm font-semibold text-slate-400 dark:text-slate-500">
                              of {stat.total}
                            </span>
                          )}
                        </p>
                        <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                          {stat.label}
                        </p>
                      </div>

                      {/* Seat usage: the roster only means something next to
                          what the company actually paid for. */}
                      {stat.total !== null && stat.total !== undefined && (
                        <div className="mt-2">
                          <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
                            <div
                              className={`h-full rounded-full transition-all ${seatsSpare < 0 ? "bg-amber-500" : "bg-primary"}`}
                              style={{ width: `${seatsUsedPct}%` }}
                            />
                          </div>
                          <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
                            {seatsSpare > 0
                              ? `${seatsSpare} seat${seatsSpare === 1 ? "" : "s"} still free`
                              : seatsSpare === 0
                                ? "All paid seats taken"
                                : `${Math.abs(seatsSpare)} over the paid count`}
                          </p>
                        </div>
                      )}

                      <p className="mt-1.5 text-xs leading-relaxed text-slate-500 dark:text-slate-400">
                        {stat.blurb}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>


      </div>

      {/* Subscription timeline — a record book of everything that happened:
          plans started/ended, seats added, upgrades (paid or super-admin
          approved) and payments, newest first. */}
      <Card className="border border-slate-200/70 dark:border-slate-700 bg-card dark:bg-slate-900 shadow-sm rounded-2xl overflow-hidden">
        <CardHeader className="py-4">
          <CardTitle className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-primary/10 border border-primary/15 flex items-center justify-center shrink-0">
              <History className="h-5 w-5 text-primary" />
            </div>
            <div className="min-w-0">
              <p className="text-sm sm:text-base font-bold text-slate-900 dark:text-foreground">
                Subscription Timeline
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Everything that happened to this company's subscription, newest first
              </p>
            </div>
            {timelineEvents.length > 0 && (
              <span className="ml-auto shrink-0 rounded-full bg-slate-100 dark:bg-slate-800 px-2 py-0.5 text-xs font-semibold text-slate-600 dark:text-slate-300">
                {timelineEvents.length}
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0 pb-5">
          {/* Period anchors, so the events below have context. Uses the same
              fallback as the card above — on a company whose subscription has
              ended this strip went blank, which read as "nothing ever ran". */}
          {displaySubscription && (
            <div className="mb-4 flex flex-wrap items-center gap-x-5 gap-y-2 rounded-xl border border-slate-200/70 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 px-4 py-3">
              <span className="text-xs font-semibold capitalize text-slate-900 dark:text-foreground">
                {displaySubscription.plan_name || displaySubscription.plan_id || "—"}
                <span className="ml-1.5 font-normal capitalize text-slate-400">
                  · {displaySubscription.billing_cycle || "—"}
                </span>
              </span>
              {subscriptionIsHistorical && (
                <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                  Last subscription
                </span>
              )}
              <span className="inline-flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-300">
                <span className="font-semibold uppercase tracking-wider text-[10px] text-slate-400">Started</span>
                {displaySubscription.start_date
                  ? formatDate(displaySubscription.start_date)
                  : "—"}
              </span>
              <ArrowRight className="h-3.5 w-3.5 text-slate-300 dark:text-slate-600" />
              <span className="inline-flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-300">
                <span className="font-semibold uppercase tracking-wider text-[10px] text-slate-400">
                  {displaySubscription.end_date || subscriptionIsHistorical ? "Ends" : "Renews"}
                </span>
                {displaySubscription.end_date
                  ? formatDate(displaySubscription.end_date)
                  : !subscriptionIsHistorical && displaySubscription.next_billing_date
                    ? formatDate(displaySubscription.next_billing_date)
                    : "—"}
              </span>
              <span className="ml-auto">
                <StatusPill status={displaySubscription.status} />
              </span>
            </div>
          )}

          {timelineEvents.length > 0 ? (
            <ol className="relative ml-3 border-l-2 border-slate-100 dark:border-slate-800">
              {visibleTimeline.map((ev, i) => {
                const m = timelineMeta(ev);
                return (
                  <li key={i} className="relative pl-7 pb-5 last:pb-0">
                    <span
                      className={`absolute -left-[15px] top-0 flex h-7 w-7 items-center justify-center rounded-full border-2 border-white dark:border-slate-900 ${m.tone}`}
                    >
                      {m.icon}
                    </span>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-900 dark:text-foreground">
                          {m.title}
                        </p>
                        {m.detail ? (
                          <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                            {m.detail}
                          </p>
                        ) : null}
                      </div>
                      <div className="shrink-0 text-right">
                        {/* {ev.amount != null && ev.amount > 0 ? (
                          <p className="text-sm font-semibold text-slate-900 dark:text-foreground">
                            {money(ev.amount, ev.currency)}
                          </p>
                        ) : null} */}
                        <p className="text-[11px] whitespace-nowrap text-slate-400">
                          {ev.at ? formatDate(ev.at) : "—"}
                        </p>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ol>
          ) : (
            <p className="py-6 text-center text-sm text-slate-500 dark:text-slate-400">
              No subscription activity yet.
            </p>
          )}

          {(timelineRemaining > 0 || timelineShown > TIMELINE_PAGE_SIZE) && (
            <div className="mt-3 flex items-center justify-center border-t border-slate-100 dark:border-slate-800 pt-3">
              <button
                type="button"
                onClick={() =>
                  setTimelineShown((shown) =>
                    timelineRemaining > 0
                      ? shown + TIMELINE_PAGE_SIZE
                      : TIMELINE_PAGE_SIZE
                  )
                }
                className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200/70 dark:border-slate-700
                  px-3.5 py-1.5 text-xs font-semibold text-slate-600 dark:text-slate-300 transition-colors
                  hover:bg-slate-50 dark:hover:bg-slate-800/50
                  focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
              >
                {timelineRemaining > 0
                  ? `Show ${Math.min(timelineRemaining, TIMELINE_PAGE_SIZE)} more`
                  : "Show less"}
                <ChevronDown
                  className={`h-3.5 w-3.5 transition-transform ${timelineRemaining > 0 ? "" : "rotate-180"}`}
                />
              </button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Purchase History: seat add-ons + plan upgrades + cycle switches */}
      <Card className="border border-slate-200/70 dark:border-slate-700 bg-card dark:bg-slate-900 shadow-sm rounded-2xl overflow-hidden">
        <CardHeader className="py-4">
          <CardTitle className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-primary/10 border border-primary/15 flex items-center justify-center">
              <Receipt className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-sm sm:text-base font-bold text-slate-900 dark:text-foreground">
                Purchase History
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Seats added, plan upgrades and billing-cycle switches bought after the plan started
              </p>
            </div>

            {/* Offline companies only. An online company's rows are written by
                the Razorpay webhook, so a hand-entered one would sit in the
                same ledger as a charge that never happened — the server
                refuses it too. */}
            {canProvision && (
              <button
                type="button"
                onClick={openAddPurchase}
                className="group ml-auto inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-slate-200
                  dark:border-slate-700 bg-card dark:bg-slate-800 px-3 py-2 text-xs font-semibold
                  text-slate-700 dark:text-slate-200 shadow-sm transition-colors
                  hover:bg-slate-50 dark:hover:bg-slate-700
                  focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-900"
              >
                <Plus className="h-3.5 w-3.5 transition-transform duration-200 group-hover:scale-110" />
                Add purchase
              </button>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0 pb-4">
          {/* Status Filter */}
          <div className="flex gap-2 mb-4 items-center">
            <Select
              value={purchasesStatusFilter}
              onValueChange={(value) => {
                setPurchasesStatusFilter(value);
                setPurchasesPage(1);
              }}
            >
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="applied">Successful</SelectItem>
                <SelectItem value="pending_payment">Pending</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
              </SelectContent>
            </Select>
            {purchasesStatusFilter !== 'all' && (
              <button
                onClick={() => {
                  setPurchasesStatusFilter('all');
                  setPurchasesPage(1);
                }}
                className="px-3 py-1.5 text-xs font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded transition"
              >
                Clear
              </button>
            )}
          </div>

          <div className="overflow-x-auto rounded-2xl border border-slate-100 dark:border-slate-800">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Change</TableHead>
                  <TableHead className="text-right">Paid</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Successful</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pagedPurchases.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-sm text-slate-500 dark:text-slate-400 py-6">
                      No purchases yet.
                    </TableCell>
                  </TableRow>
                ) : (
                  pagedPurchases.map((p) => {
                    const kind = p.kind || "seat_addon";
                    const c =
                      kind === "payment"
                        ? PAYMENT_CHIP[p.purpose_category] || PAYMENT_CHIP.unknown
                        : PURCHASE_CHIP[kind] || PURCHASE_CHIP.seat_addon;
                    const isOpen = openPurchase === String(p._id);
                    return (
                    <React.Fragment key={p._id}>
                      <TableRow
                        className={isOpen ? "bg-slate-50 dark:bg-slate-800/40" : undefined}
                      >
                        <TableCell className="whitespace-nowrap">{p.created_at ? formatDate(p.created_at) : "—"}</TableCell>
                        <TableCell>
                          <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ${c.cls}`}>
                            {c.label}
                          </span>
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-xs text-slate-600 dark:text-slate-300">
                          {kind === "payment" ? (
                            <span>
                              <span className="font-semibold text-slate-900 dark:text-foreground">
                                {p.plan_name || "—"}
                              </span>
                              {p.billing_cycle ? ` · ${capCycle(p.billing_cycle)}` : ""}
                              {p.seats != null ? ` · ${p.seats} seat${p.seats === 1 ? "" : "s"}` : ""}
                              {p.payment_method ? ` · ${p.payment_method.toUpperCase()}` : ""}
                            </span>
                          ) : kind === "seat_addon" ? (
                            <span>
                              <span className="font-semibold text-slate-900 dark:text-foreground">+{p.extra_employees}</span>{" "}
                              · {p.employees_before} &rarr; {p.employees_after} seats
                            </span>
                          ) : kind === "cycle_switch" ? (
                            <span>
                              <span className="font-semibold text-slate-900 dark:text-foreground">
                                {capCycle(p.from_billing_cycle)} &rarr; {capCycle(p.to_billing_cycle)}
                              </span>
                              {p.from_plan_name !== p.to_plan_name
                                ? ` · ${p.from_plan_name} → ${p.to_plan_name}`
                                : ` · ${p.to_plan_name}`}
                            </span>
                          ) : (
                            <span>
                              <span className="font-semibold text-slate-900 dark:text-foreground">
                                {p.from_plan_name} &rarr; {p.to_plan_name}
                              </span>
                              {p.billing_cycle ? ` · ${capCycle(p.billing_cycle)}` : ""}
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-right font-medium">{money(p.payable_now)}</TableCell>
                        <TableCell><StatusPill status={p.status} /></TableCell>
                        <TableCell className="whitespace-nowrap">{p.applied_at ? formatDate(p.applied_at) : "—"}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            {/* Offered only where the server would accept it —
                                `settleable` is the same predicate the endpoint
                                enforces (offline invoice, still outstanding). */}
                            {p.settleable && (
                              <Button size="sm" variant="outline" onClick={() => openSettle(p)}>
                                Mark paid
                              </Button>
                            )}
                            <button
                              type="button"
                              aria-expanded={isOpen}
                              aria-label={isOpen ? "Hide breakdown" : "Show breakdown"}
                              onClick={() =>
                                setOpenPurchase(isOpen ? null : String(p._id))
                              }
                              className="inline-flex items-center gap-1 rounded-lg border border-slate-200 dark:border-slate-700 px-2 py-1.5 text-xs font-medium text-slate-600 dark:text-slate-300 transition hover:bg-slate-100 dark:hover:bg-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                            >
                              View
                              <ChevronDown
                                className={`h-3.5 w-3.5 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
                              />
                            </button>
                          </div>
                        </TableCell>
                      </TableRow>

                      {/* The invoice behind the "Paid" figure. Inline rather
                          than in a dialog so two rows can be compared by
                          opening one after the other, without losing the table. */}
                      {isOpen && (
                        <TableRow className="bg-slate-50 dark:bg-slate-800/40 hover:bg-slate-50 dark:hover:bg-slate-800/40">
                          <TableCell colSpan={7} className="p-4">
                            <PurchaseBreakdown
                              purchase={p}
                              state={company?.company?.state}
                            />
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>

          {filteredPurchases.length > PURCHASES_PER_PAGE && (
            <div className="flex items-center justify-between pt-3 text-xs text-slate-500 dark:text-slate-400">
              <span>
                Showing {(purchasesPage - 1) * PURCHASES_PER_PAGE + 1}–
                {Math.min(purchasesPage * PURCHASES_PER_PAGE, filteredPurchases.length)} of{" "}
                {filteredPurchases.length}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPurchasesPage((p) => Math.max(1, p - 1))}
                  disabled={purchasesPage === 1}
                >
                  <ArrowLeft className="h-3.5 w-3.5 mr-1" /> Prev
                </Button>
                <span className="font-medium text-slate-700 dark:text-slate-200">
                  {purchasesPage} / {purchaseTotalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPurchasesPage((p) => Math.min(purchaseTotalPages, p + 1))}
                  disabled={purchasesPage === purchaseTotalPages}
                >
                  Next <ArrowRight className="h-3.5 w-3.5 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Seat Requests (off-platform companies ask the super-admin for seats) */}
      <Card className="rounded-3xl border-slate-200/70 dark:border-slate-800 shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10">
              <Users className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-sm sm:text-base font-bold text-slate-900 dark:text-foreground">
                Seat & Plan Requests
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Requests from this company's admin to add seats or upgrade the plan
              </p>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0 pb-4">
          <div className="overflow-x-auto rounded-2xl border border-slate-100 dark:border-slate-800">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Requested by</TableHead>
                  <TableHead>Seats</TableHead>
                  {/* Gross is the headline: it is what the customer is invoiced.
                      Base is shown beneath it because that is the figure
                      CompanySubscription.amount stores and the only one the
                      backfill can reconcile against. */}
                  <TableHead className="text-right">Amount (incl. GST)</TableHead>
                  <TableHead>Note</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {Array.isArray(company?.seat_requests) &&
                company.seat_requests.length > 0 ? (
                  company.seat_requests.map((r) => (
                    <TableRow key={r._id}>
                      <TableCell className="whitespace-nowrap">{r.created_at ? formatDate(r.created_at) : "—"}</TableCell>
                      <TableCell className="whitespace-nowrap">
                        <div className="font-medium">{r.requested_by_name || "—"}</div>
                        <div className="text-xs text-slate-500 dark:text-slate-400">{r.requested_by_email || ""}</div>
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        {r.type === "plan_upgrade" && (
                          <div className="mb-1 flex items-center gap-1.5">
                            <span className="inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold bg-violet-50 text-violet-700 dark:bg-violet-900/20 dark:text-violet-300">
                              Plan upgrade
                            </span>
                            <span className="text-xs text-slate-500 dark:text-slate-400">{r.plan_name || "—"}</span>
                            <span className="text-slate-300 dark:text-slate-600">&rarr;</span>
                            <span className="text-xs font-semibold text-slate-900 dark:text-foreground">{r.requested_plan_name || r.requested_plan_id || "—"}</span>
                          </div>
                        )}
                        <div className="flex items-center gap-1.5">
                          <span className="text-slate-500 dark:text-slate-400">{r.current_seats}</span>
                          <span className="text-slate-300 dark:text-slate-600">&rarr;</span>
                          <span className="font-semibold text-slate-900 dark:text-foreground">{r.approved_seats ?? r.requested_seats}</span>
                          {(() => {
                            // `r.v` does not exist. On a pending request
                            // approved_seats is null, so this was NaN — and
                            // NaN !== 0, so the badge rendered the string "NaN".
                            const d =
                              (r.approved_seats ?? r.requested_seats) - r.current_seats;
                            return Number.isFinite(d) && d !== 0 ? (
                              <span className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${d > 0 ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300" : "bg-rose-50 text-rose-700 dark:bg-rose-900/20 dark:text-rose-300"}`}>
                                {d > 0 ? `+${d}` : d}
                              </span>
                            ) : null;
                          })()}
                        </div>
                        {/* Was `{r.approved_seats} employees now` — that is the
                            SEAT count, not the headcount, and it is null while
                            the request is pending (rendering " employees now"). */}
                        <div className="text-xs text-slate-400">
                          {r.current_employees ?? "—"} employees on the books
                        </div>
                      </TableCell>

                      <TableCell className="whitespace-nowrap text-right">
                        {(() => {
                          // Prefer gross. Fall back to base only for rows written
                          // before the gross fields existed, and label it, so a
                          // base figure is never mistaken for an invoice total.
                          const after = r.amount_after_gross ?? r.amount_after;
                          const before = r.amount_before_gross ?? r.amount_before;
                          const isBase = r.amount_after_gross == null;
                          if (after == null) {
                            // Pending, or raised before the snapshot existed.
                            return <span className="text-slate-400">—</span>;
                          }
                          return (
                            <>
                              <div className="font-semibold text-slate-900 dark:text-foreground">
                                {money(after)}
                                {isBase && (
                                  <span className="ml-1 text-[10px] font-normal text-slate-400">
                                    ex-GST
                                  </span>
                                )}
                              </div>
                              {before != null && (
                                <div className="text-xs text-slate-400">
                                  from {money(before)}
                                </div>
                              )}
                              {!isBase && r.amount_after != null && (
                                <div className="text-[10px] text-slate-400">
                                  {money(r.amount_after)} + GST
                                </div>
                              )}
                              {r.price_per_user_applied != null && (
                                <div className="text-[10px] text-slate-400">
                                  {money(r.price_per_user_applied)}/user
                                  {r.billing_cycle_applied
                                    ? ` · ${capCycle(r.billing_cycle_applied)}`
                                    : ""}
                                </div>
                              )}
                            </>
                          );
                        })()}
                      </TableCell>
                      <TableCell className="max-w-[220px] text-xs text-slate-600 dark:text-slate-300">
                        {r.note || "—"}
                        {r.resolution_note ? (
                          <div className="mt-1 italic text-slate-400">“{r.resolution_note}”</div>
                        ) : null}
                      </TableCell>
                      <TableCell><StatusPill status={r.status} /></TableCell>
                      <TableCell className="text-center">
                        {r.status === "pending" ? (
                          <Button size="sm" onClick={() => openResolve(r)}>
                            Review
                          </Button>
                        ) : (
                          <span className="text-xs text-slate-400">
                            --
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-sm text-slate-500 dark:text-slate-400 py-6">
                      No seat requests.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Resolve seat request dialog */}
      <Dialog open={!!resolveTarget} onOpenChange={(v) => !resolving && !v && setResolveTarget(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {resolveTarget?.type === "plan_upgrade"
                ? "Review plan upgrade request"
                : "Review seat request"}
            </DialogTitle>
            <DialogDescription>
              {resolveTarget ? (
                <>
                  Requested by {resolveTarget.requested_by_name || "the admin"}
                  {resolveTarget.requested_by_email ? ` (${resolveTarget.requested_by_email})` : ""}
                  {resolveTarget.created_at ? ` on ${formatDate(resolveTarget.created_at)}` : ""}.
                </>
              ) : null}
            </DialogDescription>
          </DialogHeader>

          {resolveTarget && (() => {
            const sub = company?.subscription || {};
            const currency = sub.currency || "INR";
            const cycle = (sub.billing_cycle || "monthly").toLowerCase();
            const perUser =
              cycle === "annual"
                ? Number(sub.price_per_user_annual || 0)
                : Number(sub.price_per_user_monthly || 0);
            const rateSuffix = cycle === "annual" ? "/user/yr" : "/user/mo";
            const cur = Number(resolveTarget.current_seats || 0);
            const emp = Number(resolveTarget.current_employees || 0);
            const grant = Number(approveSeats || 0);
            const additional = grant - cur;
            const newAmount = perUser * grant;
            const curAmount = perUser * cur;
            const amountDelta = newAmount - curAmount;
            // Warn when granting fewer seats than the company already employs.
            const belowHeadcount = grant < emp;
            const utilPct = grant > 0 ? Math.min(100, Math.round((emp / grant) * 100)) : 0;
            // Plan's hard employee cap. -1 / null = no cap (unlimited/unknown).
            // For a PLAN UPGRADE the current plan's cap doesn't apply — the
            // server validates against the TARGET plan's cap on approval.
            const isPlanUpgradeReq = resolveTarget.type === "plan_upgrade";
            const rawCap = sub.employee_limit;
            const hasCap = !isPlanUpgradeReq && rawCap != null && Number(rawCap) !== -1;
            const cap = hasCap ? Number(rawCap) : Infinity;
            const overCap = grant > cap;

            return (
              <div className="space-y-4 py-1">
                {/* Plan upgrade banner — approval switches the plan too */}
                {resolveTarget.type === "plan_upgrade" && (
                  <div className="rounded-xl border border-violet-200 dark:border-violet-800 bg-violet-50 dark:bg-violet-900/20 px-4 py-3">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-violet-500 dark:text-violet-400 mb-1">
                      Plan upgrade request
                    </p>
                    <p className="text-sm font-medium text-violet-800 dark:text-violet-200 flex items-center gap-2">
                      <span>{resolveTarget.plan_name || "Current plan"}</span>
                      <ArrowRight className="h-4 w-4" />
                      <span className="font-bold">{resolveTarget.requested_plan_name || resolveTarget.requested_plan_id}</span>
                    </p>
                    <p className="mt-1 text-[11px] text-violet-600/80 dark:text-violet-300/80">
                      Approving switches the company to this plan (billed offline) and
                      licenses the seats below at the new plan's rate.
                    </p>
                  </div>
                )}

                {/* Seat transition hero */}
                <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-gradient-to-br from-slate-50 to-white dark:from-slate-800/60 dark:to-slate-900 p-4">
                  <div className="flex items-center justify-center gap-4">
                    <div className="text-center">
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Current</p>
                      <p className="text-2xl font-bold leading-tight text-slate-900 dark:text-foreground">{cur}</p>
                      <p className="text-[11px] text-slate-400">seats</p>
                    </div>
                    <div className="flex flex-col items-center">
                      <span className={`inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 text-[11px] font-semibold ${additional >= 0 ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300" : "bg-rose-50 text-rose-700 dark:bg-rose-900/20 dark:text-rose-300"}`}>
                        {additional >= 0 ? `+${additional}` : additional}
                      </span>
                      <ArrowRight className="mt-1 h-5 w-5 text-slate-300 dark:text-slate-600" />
                    </div>
                    <div className="text-center">
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-primary/70">Granting</p>
                      <p className="text-2xl font-bold leading-tight text-primary">{grant}</p>
                      <p className="text-[11px] text-slate-400">seats</p>
                    </div>
                  </div>

                  {/* Utilisation bar */}
                  <div className="mt-4">
                    <div className="mb-1 flex items-center justify-between text-[11px] text-slate-500 dark:text-slate-400">
                      <span>{emp} of {grant} seats used</span>
                      <span>{utilPct}%</span>
                    </div>
                    <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700">
                      <div
                        className={`h-full rounded-full transition-all ${belowHeadcount ? "bg-rose-500" : "bg-emerald-500"}`}
                        style={{ width: `${Math.max(4, utilPct)}%` }}
                      />
                    </div>
                  </div>
                </div>

                {/* Plan meta */}
                <div className="flex items-center justify-between rounded-lg bg-slate-50 dark:bg-slate-800/40 px-3 py-2 text-xs">
                  <span className="font-medium capitalize text-slate-700 dark:text-slate-200">
                    {sub.plan_name || sub.plan_id || "—"}
                    <span className="ml-1 font-normal text-slate-400">· {cycle}</span>
                  </span>
                  <span className="flex items-center gap-2 font-medium text-slate-700 dark:text-slate-200">
                    <span>
                      {money(perUser, currency)}
                      <span className="font-normal text-slate-400">{rateSuffix}</span>
                    </span>
                    <span className="text-slate-300 dark:text-slate-600">|</span>
                    <span className="font-normal text-slate-400">
                      Cap {hasCap ? cap : "Unlimited"}
                    </span>
                  </span>
                </div>

                {resolveTarget.note ? (
                  <div className="rounded-lg border-l-2 border-primary/40 bg-slate-50 dark:bg-slate-800/40 px-3 py-2">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-0.5">Admin's note</p>
                    <p className="text-sm italic text-slate-600 dark:text-slate-300">“{resolveTarget.note}”</p>
                  </div>
                ) : null}

                {/* Editable grant */}
                <div className="space-y-1.5">
                  <Label htmlFor="approve-seats" className="text-xs font-semibold text-slate-600 dark:text-slate-300">
                    Seats to grant
                  </Label>
                  <Input
                    id="approve-seats"
                    type="number"
                    min={1}
                    max={hasCap ? cap : undefined}
                    value={approveSeats}
                    onChange={(e) => setApproveSeats(Math.max(1, parseInt(e.target.value, 10) || 1))}
                    disabled={resolving}
                    className="w-32"
                  />
                  {belowHeadcount ? (
                    <p className="flex items-center gap-1 text-xs font-medium text-rose-600 dark:text-rose-400">
                      <AlertCircle className="h-3.5 w-3.5" />
                      Can't be below the {emp} current employees.
                    </p>
                  ) : overCap ? (
                    <p className="flex items-center gap-1 text-xs font-medium text-rose-600 dark:text-rose-400">
                      <AlertCircle className="h-3.5 w-3.5" />
                      Exceeds the {sub.plan_name || "plan"}'s cap of {cap}. Upgrade the plan to license more.
                    </p>
                  ) : (
                    hasCap && (
                      <p className="text-xs text-slate-400">
                        {cap - grant} of {cap} plan seats remaining.
                      </p>
                    )
                  )}
                </div>

                {/* Billing impact — the commercial focal point. For a plan
                    upgrade the CURRENT plan's rate would mislead, so show a
                    note instead: the server recomputes at the new plan's rate. */}
                {isPlanUpgradeReq ? (
                  <p className="rounded-xl border border-primary/20 bg-primary/5 px-4 py-3 text-xs text-slate-600 dark:text-slate-300">
                    Billing is recalculated at <b>{resolveTarget.requested_plan_name || "the new plan"}</b>'s
                    per-user rate ({cycle}) for {grant} seats on approval.
                  </p>
                ) : (
                  <div className="overflow-hidden rounded-xl border border-primary/20">
                    <div className="flex items-center justify-between bg-primary/5 px-4 py-3">
                      <div>
                        <p className="flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider text-primary/70">
                          <TrendingUp className="h-3.5 w-3.5" /> New recurring · {cycle}
                        </p>
                        <p className="text-2xl font-bold text-slate-900 dark:text-foreground">
                          {money(newAmount, currency)}
                        </p>
                      </div>
                      <div className="text-right text-xs">
                        <p className="text-slate-400 line-through">{money(curAmount, currency)}</p>
                        <p className={`font-semibold ${amountDelta >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-rose-600 dark:text-rose-400"}`}>
                          {amountDelta >= 0 ? "+" : "−"}{money(Math.abs(amountDelta), currency)}
                        </p>
                      </div>
                    </div>
                    <p className="bg-card dark:bg-slate-900 px-4 py-2 text-[11px] text-slate-400">
                      {grant} seats × {money(perUser, currency)}{rateSuffix}. Applied as the licensed
                      seat count and billing amount on approval.
                    </p>
                  </div>
                )}

                <div className="space-y-1.5">
                  <Label htmlFor="resolve-note" className="text-xs font-semibold text-slate-600 dark:text-slate-300">
                    Note to admin <span className="font-normal text-slate-400">(optional)</span>
                  </Label>
                  <Textarea
                    id="resolve-note"
                    value={resolveNote}
                    maxLength={500}
                    rows={2}
                    onChange={(e) => setResolveNote(e.target.value)}
                    disabled={resolving}
                    placeholder="Visible to the company admin."
                  />
                </div>
              </div>
            );
          })()}

          <DialogFooter className="flex justify-between gap-2 sm:justify-between">
            <Button
              variant="outline"
              className="border-rose-200 text-rose-600 hover:bg-rose-50 hover:text-rose-700 dark:border-rose-900/40 dark:hover:bg-rose-900/20"
              onClick={() => submitResolve("reject")}
              disabled={resolving}
            >
              {resolving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><X className="mr-1.5 h-4 w-4" /> Reject</>}
            </Button>
            {(() => {
              // A plan upgrade is validated against the TARGET plan's cap
              // server-side — the current plan's cap doesn't apply here.
              const capRaw = company?.subscription?.employee_limit;
              const capActive =
                resolveTarget?.type !== "plan_upgrade" &&
                capRaw != null &&
                Number(capRaw) !== -1;
              const overCap = capActive && Number(approveSeats) > Number(capRaw);
              const belowHeadcount =
                Number(approveSeats) < Number(resolveTarget?.current_employees || 0);
              return (
                <Button
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  onClick={() => submitResolve("approve")}
                  disabled={resolving || belowHeadcount || overCap}
                >
                  {resolving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Check className="mr-1.5 h-4 w-4" /> {resolveTarget?.type === "plan_upgrade" ? `Approve upgrade · ${approveSeats} seats` : `Approve ${approveSeats} seats`}</>}
                </Button>
              );
            })()}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Direct provisioning dialog (offline-billed companies) */}
      <ProvisionSubscriptionDialog company={company} provision={provision} />

      {/* Settle a pending offline invoice raised by provisioning. */}
      <Dialog open={!!settleTarget} onOpenChange={(v) => !settling && !v && setSettleTarget(null)}>
        <DialogContent className="max-w-md dark:bg-slate-900">
          <DialogHeader className="space-y-2">
            <DialogTitle className="flex items-center gap-2.5">
              <span className="h-8 w-8 shrink-0 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center">
                <Receipt className="h-4 w-4 text-primary" />
              </span>
              Mark as paid
            </DialogTitle>
            <DialogDescription>
              Records that this invoice was settled outside the platform. It does
              not change the subscription — the seats or plan were already
              applied when it was raised.
            </DialogDescription>
          </DialogHeader>

          {settleTarget && (
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg bg-slate-100 dark:bg-slate-800/60 px-3 py-2 text-xs text-slate-600 dark:text-slate-300">
              <span className="text-slate-400 dark:text-slate-500 uppercase tracking-wide text-[10px] font-semibold">
                Invoice
              </span>
              <span className="font-semibold text-slate-900 dark:text-foreground">
                {money(settleTarget.payable_now, settleTarget.currency)}
              </span>
              <span className="text-slate-300 dark:text-slate-600">·</span>
              <span>{settleTarget.purpose_label}</span>
              {(settleTarget.amount_basis === "delta" ||
                settleTarget.amount_basis === "prorated") && (
                <>
                  <span className="text-slate-300 dark:text-slate-600">·</span>
                  {/* The row is a difference, not a term total — say so, or the
                      figure looks wrong next to the subscription's amount. A
                      prorated row covers only part of the cycle, so name the
                      window: the amount cannot be derived without it. */}
                  <span className="text-slate-400">
                    {settleTarget.amount_basis === "prorated"
                      ? settleTarget.remaining_months != null &&
                        settleTarget.total_cycle_months != null
                        ? `prorated · ${settleTarget.remaining_months} of ${settleTarget.total_cycle_months} months`
                        : "prorated"
                      : "increase only"}
                  </span>
                </>
              )}
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="settle-date">Date received</Label>
            <Input
              id="settle-date"
              type="date"
              value={settleDate}
              max={new Date().toISOString().slice(0, 10)}
              onChange={(e) => setSettleDate(e.target.value)}
            />
            <p className="text-[11px] text-slate-400">
              Backdate it to the day the money actually arrived. The server
              rejects a future date.
            </p>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setSettleTarget(null)}
              disabled={settling}
            >
              Cancel
            </Button>
            <Button onClick={submitSettle} disabled={settling || !settleDate}>
              {settling ? "Saving…" : "Mark as paid"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Record a payment collected outside the platform. */}
      <Dialog open={purchaseOpen} onOpenChange={(v) => !savingPurchase && setPurchaseOpen(v)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto dark:bg-slate-900">
          <DialogHeader className="space-y-2">
            <DialogTitle className="flex items-center gap-2.5">
              <span className="h-8 w-8 shrink-0 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center">
                <Receipt className="h-4 w-4 text-primary" />
              </span>
              Add purchase
            </DialogTitle>
            <DialogDescription>
              Records a payment{" "}
              <span className="font-medium text-slate-700 dark:text-slate-200">
                {company?.company?.company_name}
              </span>{" "}
              made outside the platform, so it appears in Purchase History. This
              only writes the record — it does not extend the subscription.
            </DialogDescription>
          </DialogHeader>

          {/* Taken from the subscription, not typed. Shown so the super-admin
              can see what the row will say before writing it. */}
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg bg-slate-100 dark:bg-slate-800/60 px-3 py-2 text-xs text-slate-600 dark:text-slate-300">
            <span className="text-slate-400 dark:text-slate-500 uppercase tracking-wide text-[10px] font-semibold">
              For
            </span>
            <span className="font-semibold text-slate-900 dark:text-foreground">
              {purchaseQuote.plan_name}
            </span>
            <span className="text-slate-300 dark:text-slate-600">·</span>
            <span className="capitalize">{purchaseQuote.cycle}</span>
            <span className="text-slate-300 dark:text-slate-600">·</span>
            <span>
              {purchaseQuote.seats} seat{purchaseQuote.seats === 1 ? "" : "s"}
            </span>
          </div>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>
                Payment for <span className="text-red-500">*</span>
              </Label>
              <Select value={purchaseType} onValueChange={changePurchaseType}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {/* Values map to Payment purposes server-side
                      (OFFLINE_PURCHASE_PURPOSES) — the labels here match the
                      chips the Purchase History table renders. */}
                  <SelectItem value="renewal">Renewal</SelectItem>
                  <SelectItem value="signup">New subscription</SelectItem>
                  <SelectItem value="seat_addon">Seat add-on</SelectItem>
                  <SelectItem value="plan_upgrade">Plan upgrade</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="purchase-date">
                Payment received on <span className="text-red-500">*</span>
              </Label>
              <input
                id="purchase-date"
                type="date"
                required
                value={purchaseDate}
                onChange={(e) => setPurchaseDate(e.target.value)}
                className="w-full rounded-md border border-slate-300 dark:border-slate-700 px-3 py-2 bg-background text-sm"
              />
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Backdate this to when the money actually arrived — the row files
                itself under that date, not today.
              </p>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="purchase-amount">
                Amount (incl. GST) <span className="text-red-500">*</span>
              </Label>
              <Input
                id="purchase-amount"
                type="number"
                min="0"
                step="0.01"
                value={purchaseAmount}
                onChange={(e) => setPurchaseAmount(e.target.value)}
              />
              <p className="text-xs text-slate-500 dark:text-slate-400">
                {purchaseQuote.gross == null ? (
                  // No rate on the subscription means nothing to quote; say so
                  // rather than leaving an unexplained empty field.
                  "This subscription carries no per-user rate, so enter the invoiced amount."
                ) : purchaseType === "renewal" || purchaseType === "signup" ? (
                  <>
                    Pre-filled with what this subscription bills —{" "}
                    <span className="font-medium">
                      {money(purchaseQuote.gross, purchaseQuote.currency)}
                    </span>
                    . Edit it for a part payment or a one-off figure.
                  </>
                ) : (
                  <>
                    Enter what was actually invoiced for this change — a{" "}
                    {purchaseType === "seat_addon" ? "seat add-on" : "mid-term upgrade"} is
                    normally a part charge, not the full{" "}
                    <span className="font-medium">
                      {money(purchaseQuote.gross, purchaseQuote.currency)}
                    </span>
                    .
                  </>
                )}
              </p>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="purchase-note">Note</Label>
              <Textarea
                id="purchase-note"
                rows={2}
                placeholder="Invoice number, cheque/NEFT reference, anything worth keeping."
                value={purchaseNote}
                onChange={(e) => setPurchaseNote(e.target.value)}
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              disabled={savingPurchase}
              onClick={() => setPurchaseOpen(false)}
            >
              Cancel
            </Button>
            <Button
              // Catch what the API would reject anyway, so the user finds out
              // before the round trip. `savingPurchase` also blocks the
              // double-click that would otherwise book the payment twice.
              disabled={
                savingPurchase ||
                !purchaseDate ||
                purchaseAmount === "" ||
                !(Number(purchaseAmount) > 0)
              }
              onClick={submitPurchase}
            >
              {savingPurchase ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add record"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CompanyDetails;
